import { formatDate as formatCanonicalDate } from "@donoor/utils";

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  sort: number;
}

export interface BlogAuthor {
  id: string;
  name: string;
  role: string;
  bio?: string;
  avatar?: string;
}

export interface BlogArticle {
  id: string;
  title: string;
  slug: string;
  description: string;
  content?: string;
  image?: string;
  read_time?: string;
  featured: boolean;
  date_published: string;
  date_modified?: string;
  category: BlogCategory;
  author: BlogAuthor;
}

interface ArticlesResponse {
  articles: BlogArticle[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useBlog() {
  const config = useRuntimeConfig();
  const assetsBaseUrl = config.public.apiUrl as string;

  function getImageUrl(imageId: string | undefined | null, width = 800) {
    if (!imageId) return "/og-image.png";
    return `${assetsBaseUrl}/api/assets/${imageId}?width=${width}&format=webp&quality=80`;
  }

  // Image brute, sans transformation — sert de filet via onImageError quand
  // l'image transformée échoue (source dépassant ASSETS_TRANSFORM_IMAGE_MAX_DIMENSION
  // → 400 Directus, ou asset supprimé). Même pattern que useRechercheHelpers.ts.
  function getRawImageUrl(imageId: string | undefined | null) {
    if (!imageId) return "/og-image.png";
    return `${assetsBaseUrl}/api/assets/${imageId}`;
  }

  // Handler @error d'une <img> : si l'URL transformée échoue, on bascule UNE
  // fois vers l'image brute. `data-fallback` empêche une boucle si la brute
  // échoue aussi. L'<img> doit porter :data-asset-id="article.image".
  // `removeAttribute("srcset")` : sur les <img> avec srcset (hero LCP
  // responsive), le navigateur privilégie srcset sur src — sans le retirer,
  // les variantes srcset (toutes générées via la même transformation
  // Directus en échec) referaient échouer l'image malgré le nouveau src.
  function onImageError(event: Event | string) {
    if (typeof event === "string") return;
    const img = event.target as HTMLImageElement | null;
    if (!img || img.dataset.fallback === "done") return;
    const id = img.dataset.assetId;
    img.dataset.fallback = "done";
    if (id) {
      img.removeAttribute("srcset");
      img.src = getRawImageUrl(id);
    } else {
      img.style.display = "none";
    }
  }

  function getAvatarUrl(avatarId: string | undefined | null) {
    // Fallback = icône PWA existante — /logos/donoor-icon.svg n'a jamais
    // existé dans public/ (5 images 404 crawlées, audit SemRush 2026-08-05).
    if (!avatarId) return "/icons/icon-192x192.png";
    return `${assetsBaseUrl}/api/assets/${avatarId}?width=100&height=100&fit=cover&format=webp`;
  }

  async function fetchArticles(options: {
    page?: number;
    limit?: number;
    category?: string;
    featured?: boolean;
  } = {}) {
    const params: Record<string, string> = {};
    if (options.page) params.page = String(options.page);
    if (options.limit) params.limit = String(options.limit);
    if (options.category) params.category = options.category;
    if (options.featured) params.featured = "true";

    return useFetch<ArticlesResponse>("/api/blog/articles", {
      params,
      key: `blog-articles-${options.category || "all"}-${options.page || 1}-${options.featured || false}`,
    });
  }

  async function fetchArticle(slug: string) {
    return useFetch<BlogArticle>(`/api/blog/articles/${slug}`, {
      key: `blog-article-${slug}`,
    });
  }

  async function fetchCategories() {
    return useFetch<BlogCategory[]>("/api/blog/categories", {
      key: "blog-categories",
    });
  }

  function formatDate(dateStr: string) {
    if (!dateStr) return "";
    return formatCanonicalDate(dateStr, { format: "medium" });
  }

  return {
    getImageUrl,
    getRawImageUrl,
    onImageError,
    getAvatarUrl,
    fetchArticles,
    fetchArticle,
    fetchCategories,
    formatDate,
  };
}
